import { useState, useEffect } from "react";
import SVGraphics from "../../../assets/SVGraphics";
import { formatDate } from "./helpers/helpers";
import RadiologistApi from "../../../services/ApiServices/RadiologistApi";
import { annotation as annotationState } from "@cornerstonejs/tools";
import { useViewer } from "../DicomViewerNew/context/ViewerContext";
import { viewport } from "@cornerstonejs/tools/utilities";

const NewAnnotation = ({
  annotation,
  examData,
  leftViewportRef,
  onClose,
  mode = "measurement",
  currentSlice,
  currentUser: propCurrentUser,
}) => {
  const [annotationText, setAnnotationText] = useState("");
  const [currentUser, setCurrentUser] = useState(propCurrentUser || null);
  const { setActiveTool } = useViewer();
  const { addAnnotationToData, upsertAnnotationInData } = useViewer();

  const isPersonalNote = mode === "personal";

  useEffect(() => {
    if (!propCurrentUser) {
      const loggedInUser = sessionStorage.getItem("loggedInUser");
      if (loggedInUser) {
        try {
          const user = JSON.parse(loggedInUser);
          setCurrentUser(user);
          console.log("User loaded from sessionStorage:", user);
        } catch (error) {
          console.error("Error parsing user from sessionStorage:", error);
        }
      }
    }
  }, [propCurrentUser]);

  // Unified save function for both types
  const handleSave = async (shouldSave) => {
    if (!shouldSave) {
      if (!isPersonalNote) {
        deleteAnnotationFromCanvas();
      }
      onClose();
      return;
    }

    if (isPersonalNote && !annotationText.trim()) {
      deleteAnnotationFromCanvas();
      onClose();
      return;
    }

    const payload = {
      creator: `${currentUser?.profile?.First_name} ${currentUser?.profile?.Last_name}`,
      sliceIndex: isPersonalNote ? currentSlice - 1 : annotation.slice,
      examId: examData.caseId,
      note: annotationText,
      type: isPersonalNote ? "personal" : "measurement",
      x: isPersonalNote ? 0 : annotation.end[0],
      y: isPersonalNote ? 0 : annotation.end[1],
      start: isPersonalNote
        ? JSON.stringify({ x: 0, y: 0 })
        : JSON.stringify({ x: annotation.start[0], y: annotation.start[1] }),
      end: isPersonalNote
        ? JSON.stringify({ x: 0, y: 0 })
        : JSON.stringify({ x: annotation.end[0], y: annotation.end[1] }),
      length: isPersonalNote ? 0 : annotation.length,
      measurementData: isPersonalNote
        ? JSON.stringify({
            isPersonalNote: true,
            note: annotationText,
          })
        : JSON.stringify({
            start: { x: annotation.start[0], y: annotation.start[1] },
            end: { x: annotation.end[0], y: annotation.end[1] },
            length: annotation.length,
            x: annotation.end[0],
            y: annotation.end[1],
          }),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    try {
      const savedAnnotation = await RadiologistApi.addAnnotation(payload);
      const saved = savedAnnotation?.data?.annotation;
      if (saved) {
        const newAnnotation = isPersonalNote
          ? {
              id: saved.id,
              creator: saved.creator,
              note: annotationText,
              updatedAt: saved.updatedAt,
              slice: currentSlice - 1,
              type: "personal",
            }
          : {
              id: saved.id,
              creator: saved.creator,
              note: annotationText,
              updatedAt: saved.updatedAt,
              x: annotation.end[0],
              y: annotation.end[1],
              start: { x: annotation.start[0], y: annotation.start[1] },
              end: { x: annotation.end[0], y: annotation.end[1] },
              length: annotation.length,
              slice: annotation.slice,
              type: "measurement",
            };

        addAnnotationToData(
          newAnnotation,
          isPersonalNote ? currentSlice - 1 : annotation.slice
        );
        if (!isPersonalNote) {
          upsertAnnotationInData(newAnnotation, annotation.slice);
        }
      }

      if (!isPersonalNote) {
        deleteAnnotationFromCanvas();
        setActiveTool("MouseIcon");
      }
    } catch (error) {
      console.error("Failed to save annotation:", error);
      if (!isPersonalNote) {
        deleteAnnotationFromCanvas();
      }
    }

    onClose();
  };

  const deleteAnnotationFromCanvas = () => {
    try {
      // Remove annotation from Cornerstone state
      annotationState.state.removeAnnotation(annotation.data.annotationUID);

      // Force viewport to re-render to remove visual markers
      // Note: leftViewportRef is already the viewport object (not a ref with .current)
      if (leftViewportRef) {
        // Trigger viewport render to clear the annotation visuals
        leftViewportRef.render();

        // Alternative: If render doesn't work, try invalidating the cache
        if (typeof leftViewportRef.invalidateCache === "function") {
          leftViewportRef.invalidateCache();
          leftViewportRef.render();
        }

        // If the viewport has a canvas element, force a redraw
        const canvas = leftViewportRef.canvas;
        if (canvas) {
          const context = canvas.getContext("2d");
          if (context) {
            // Clear and redraw the canvas
            context.clearRect(0, 0, canvas.width, canvas.height);
            leftViewportRef.render();
          }
        }

        // Try to get the rendering engine from the viewport
        if (leftViewportRef.renderingEngine) {
          leftViewportRef.renderingEngine.render();
        }
      }

      console.log(
        "Annotation and markers removed:",
        annotation.data.annotationUID
      );
    } catch (err) {
      console.error("Failed to remove annotation from canvas:", err);
    }
  };

  // Render personal note form inline in the panel
  if (isPersonalNote) {
    return (
      <div className="annotation-tile max-w-[15rem] flex flex-col py-[15px] pl-[8px] pr-[9px] text-white bg-transparent border-b border-[#2B2B2B] mb-4">
        {/* Header */}
        <div className="flex items-center justify-between py-2">
          <div className="modal-header flex items-center">
            <SVGraphics svgname="personalNote" width="20" height="20" />
            <p className="pl-2">Personal Note</p>
          </div>
        </div>

        <div className="py-2 space-y-1">
          <div className="flex text-gray-400 text-xs">
            <span className="whitespace-nowrap">
              {currentUser?.profile?.First_name}{" "}
              {currentUser?.profile?.Last_name}
            </span>
            <span className="mx-2">|</span>
            <span className="whitespace-nowrap">
              {new Date()
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace(",", " -")}
            </span>
          </div>
          <div className="text-gray-400 text-xs"># Slice {currentSlice}</div>

          <textarea
            className="bg-black w-full text-[12px] h-[100px] p-[9px] rounded-[10px] mt-2"
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            placeholder="Add your personal note..."
          />

          <div className="flex flex-row-reverse gap-2 mt-2">
            <button
              onClick={() => handleSave(true)}
              className="text-[12px] bg-[#5A3EFF] rounded-[8px] w-[81px] h-[32px]">
              Save
            </button>
            <button
              onClick={() => handleSave(false)}
              className="text-[12px] text-white border rounded-[8px] w-[81px] h-[32px]">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Original measurement annotation logic
  if (!annotation || !annotation.end) return null;

  const getPosition = () => {
    try {
      // Use midpoint for better positioning
      const midWorldX = (annotation.start[0] + annotation.end[0]) / 2;
      const midWorldY = (annotation.start[1] + annotation.end[1]) / 2;

      const [canvasX, canvasY] = leftViewportRef.worldToCanvas([
        midWorldX,
        midWorldY,
        0,
      ]);

      console.log("canvasX", canvasX);
      console.log("canvasY", canvasY);

      const popupWidth = 275;
      const popupHeight = 200;
      const offset = 20;

      // Get viewport dimensions
      const viewportElement =
        leftViewportRef.current || leftViewportRef.element;
      const viewportRect = viewportElement
        ? viewportElement.getBoundingClientRect()
        : { width: 800, height: 600 };
      const viewportWidth = viewportRect.width;
      const viewportHeight = viewportRect.height;

      let x = canvasX;
      let y = canvasY;

      // Horizontal positioning logic
      // Check if there's enough space on the right
      if (canvasX + popupWidth + offset > viewportWidth) {
        // Not enough space on the right, position to the left
        x = canvasX - popupWidth - offset;

        // If still not enough space on the left, center it as best as possible
        if (x < 0) {
          x = Math.max(0, (viewportWidth - popupWidth) / 2);
        }
      } else {
        // Enough space on the right, position with offset
        x = canvasX + offset;
      }

      // Vertical positioning logic
      // Check if there's enough space below
      if (canvasY + popupHeight + offset > viewportHeight) {
        // Not enough space below, position above
        y = canvasY - popupHeight - offset;

        // If still not enough space above, position at top
        if (y < 0) {
          y = offset;
        }
      } else {
        // Enough space below, position with offset
        y = canvasY + offset;
      }

      // Ensure popup stays within viewport bounds
      x = Math.max(0, Math.min(x, viewportWidth - popupWidth));
      y = Math.max(0, Math.min(y, viewportHeight - popupHeight));

      return { x, y };
    } catch (error) {
      console.error("Error in getPosition:", error);
      return { x: 100, y: 100 };
    }
  };
  const { x, y } = getPosition();

  return (
    <div
      className="annotation-modal absolute flex flex-col gap-[10px] w-[275px] bg-[#212121] text-white rounded-[10px] p-4 shadow-lg z-[60] pointer-events-auto"
      style={{
        left: `${x}px`,
        bottom: `${y}px`,
      }}>
      <div className="modal-header flex items-center">
        <SVGraphics svgname="MeasureModalIcon" width="30" height="30" />
        <p className="pl-2">Measuring Note</p>
      </div>

      <div className="flex flex-col text-sm text-[#C1C1C1]">
        <p>
          {currentUser?.profile?.First_name} {currentUser?.profile?.Last_name} |{" "}
          {formatDate(new Date())}
        </p>
        <p>
          Slice: <span className="text-white">{annotation.slice}</span>
        </p>
        <p>
          Length:{" "}
          <span className="text-white">{annotation.length?.toFixed(2)} mm</span>
        </p>
      </div>

      <textarea
        style={{ resize: "none" }}
        value={annotationText}
        onChange={(e) => setAnnotationText(e.target.value)}
        className="bg-[#1a1a1a] w-full text-[14px] h-[75px] p-3 rounded-[8px] text-white placeholder:text-gray-400 outline-none border border-[#333]"
        placeholder="Add Note"
      />

      <div className="flex flex-row-reverse gap-2">
        <button
          onClick={() => handleSave(true)}
          className="text-[12px] bg-[#5A3EFF] rounded-[8px] w-[81px] h-[32px]">
          Save
        </button>
        <button
          onClick={() => handleSave(false)}
          className="text-[12px] border border-white rounded-[8px] w-[81px] h-[32px]">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default NewAnnotation;
